import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Zap, Calendar, Trash2, FileText, Download, Clock } from "lucide-react";
import { format } from "date-fns";
import AddToCollection from "@/components/AddToCollection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function ReadingLog({ api }) {
  const navigate = useNavigate();
  const [log, setLog] = useState([]);
  const [allPapers, setAllPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [logRes, papersRes] = await Promise.all([
          axios.get(`${api}/reading-log`, { withCredentials: true }),
          axios.get(`${api}/papers`, { withCredentials: true }),
        ]);
        setLog(logRes.data);
        setAllPapers(papersRes.data);
      } catch (e) {
        toast.error("Failed to load reading log");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const handleDelete = async (paperId) => {
    try {
      await axios.delete(`${api}/papers/${paperId}`, { withCredentials: true });
      setLog((prev) => prev.filter((p) => p.id !== paperId));
      setAllPapers((prev) => prev.filter((p) => p.id !== paperId));
      toast.success("Paper removed");
    } catch (e) {
      toast.error("Failed to delete paper");
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${api}/reading-log/export/csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "reading_log.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported!");
    } catch (e) {
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${api}/reading-log/export/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "reading_log.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF exported!");
    } catch (e) {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const filteredLog = filter === "all" ? log : log.filter((p) => p.paper_type === filter);
  const inProgressPapers = allPapers.filter((p) => p.status === "in_progress");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="reading-log-loading">
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center p-8">
          <div className="w-12 h-12 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading font-bold text-lg dark:text-white">Loading reading log...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reading-log-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight dark:text-white">Reading Log</h1>
          <p className="text-neo-text-secondary dark:text-gray-400 mt-1 text-base">Track your academic reading journey</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white flex items-center gap-2" data-testid="filter-dropdown-trigger">
                <FileText className="w-4 h-4" />
                {filter === "all" ? "All Types" : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="border-2 border-neo-border dark:border-[#444] shadow-[3px_3px_0px_0px_#1A1A1A] dark:shadow-[3px_3px_0px_0px_#444] dark:bg-[#1E1E1E]">
              <DropdownMenuItem onClick={() => setFilter("all")} className="dark:text-white" data-testid="filter-all">All Types</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("empirical")} className="dark:text-white" data-testid="filter-empirical">Empirical</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("conceptual")} className="dark:text-white" data-testid="filter-conceptual">Conceptual</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("review")} className="dark:text-white" data-testid="filter-review">Review</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilter("meta-analysis")} className="dark:text-white" data-testid="filter-meta">Meta-Analysis</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Export Buttons */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="neo-btn neo-btn-primary flex items-center gap-2" disabled={exporting} data-testid="export-dropdown-trigger">
                <Download className="w-4 h-4" />
                {exporting ? "Exporting..." : "Export"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-2 border-neo-border dark:border-[#444] shadow-[3px_3px_0px_0px_#1A1A1A] dark:shadow-[3px_3px_0px_0px_#444] dark:bg-[#1E1E1E]">
              <DropdownMenuItem onClick={handleExportCSV} className="dark:text-white" data-testid="export-csv-btn">
                <FileText className="w-4 h-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF} className="dark:text-white" data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* In Progress */}
      {inProgressPapers.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-xl mb-3 flex items-center gap-2 dark:text-white">
            <div className="w-3 h-3 rounded-full bg-neo-yellow border-2 border-neo-border dark:border-[#444]"></div>
            In Progress ({inProgressPapers.length})
          </h2>
          <div className="space-y-3">
            {inProgressPapers.map((paper) => (
              <div
                key={paper.id}
                className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] neo-card-hover flex items-center gap-4 cursor-pointer"
                onClick={() => navigate(`/reader/${paper.id}`)}
                data-testid={`in-progress-paper-${paper.id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-neo-yellow border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-neo-text" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold truncate dark:text-white">{paper.title}</h3>
                  <div className="flex items-center gap-3 text-sm text-neo-text-secondary dark:text-gray-400 mt-1">
                    <span className={`neo-badge text-xs ${paper.paper_type === "empirical" ? "bg-neo-blue" : "bg-neo-purple"}`}>
                      {paper.paper_type}
                    </span>
                    <span>{paper.current_page + 1}/{paper.total_pages} pages</span>
                  </div>
                </div>
                <div className="neo-badge bg-neo-yellow text-xs">
                  <Zap className="w-3 h-3" /> {paper.xp_earned} XP
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed */}
      <div>
        <h2 className="font-heading font-bold text-xl mb-3 flex items-center gap-2 dark:text-white">
          <div className="w-3 h-3 rounded-full bg-neo-green border-2 border-neo-border dark:border-[#444]"></div>
          Completed ({filteredLog.length})
        </h2>
        {filteredLog.length === 0 ? (
          <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-12" data-testid="empty-reading-log">
            <BookOpen className="w-12 h-12 text-neo-text-secondary dark:text-gray-500 mx-auto mb-3" />
            <p className="font-heading font-bold text-lg mb-1 dark:text-white">No completed papers yet</p>
            <p className="text-sm text-neo-text-secondary dark:text-gray-400">Start reading to build your log!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="completed-papers-list">
            {filteredLog.map((entry, i) => (
              <div key={entry.id} className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] flex items-center gap-4 slide-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`completed-paper-${entry.id}`}>
                <div className="w-12 h-12 rounded-lg bg-neo-green border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-neo-text" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold truncate dark:text-white">{entry.title}</h3>
                  <div className="flex items-center gap-3 text-sm text-neo-text-secondary dark:text-gray-400 mt-1 flex-wrap">
                    <span className={`neo-badge text-xs ${
                      entry.paper_type === "empirical" ? "bg-neo-blue" :
                      entry.paper_type === "conceptual" ? "bg-neo-purple" :
                      entry.paper_type === "review" ? "bg-neo-green" : "bg-neo-yellow"
                    }`}>
                      {entry.paper_type}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {entry.completed_at ? format(new Date(entry.completed_at), "MMM d, yyyy") : "N/A"}
                    </span>
                    <span>{entry.total_pages} pages</span>
                    {entry.time_spent_seconds > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {(() => {
                          const s = entry.time_spent_seconds;
                          const h = Math.floor(s / 3600);
                          const m = Math.floor((s % 3600) / 60);
                          return h > 0 ? `${h}h ${m}m` : `${m}m`;
                        })()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="neo-badge bg-neo-yellow text-xs">
                    <Zap className="w-3 h-3" /> {entry.xp_earned} XP
                  </div>
                  <AddToCollection api={api} paperId={entry.id} />
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }} className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] p-2" data-testid={`delete-paper-${entry.id}`}>
                    <Trash2 className="w-4 h-4 text-neo-red" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
